<div
  class="generic-schedule"
  [ngClass]="{
    'generic-schedule__changed':
      scheduleService().isFilledAndChanged() &&
      !readonly() &&
      (scheduleService().isChanged$ | async),
    'generic-schedule__disabled': isDisabled() && !readonly(),
  }"
>
  <div class="generic-schedule__header">
    <h2 class="generic-schedule__title">
      {{ title() }}
      @if (readonly()) {
        <button
          mat-icon-button
          (click)="onExportJson()"
          [matTooltip]="strings.tooltip.exportScheduleJson"
          [disabled]="!canExportJson()"
          [attr.data-testid]="'export-schedule-button'"
          color="accent"
        >
          <mat-icon>file_download</mat-icon>
        </button>
      }
      @if (!readonly()) {
        <mat-icon
          (mouseover)="onTooltipMouseEnter()"
          (mouseleave)="onTooltipMouseLeave()"
          (focus)="onTooltipMouseEnter()"
          (blur)="onTooltipMouseLeave()"
          tabindex="0"
          aria-label="Schedule help"
          cdkOverlayOrigin
          #trigger="cdkOverlayOrigin"
        >
          help_outline
          <ng-template
            cdkConnectedOverlay
            [cdkConnectedOverlayOrigin]="trigger"
            [cdkConnectedOverlayOpen]="isTooltipOpen()"
          >
            <div
              class="generic-schedule__tooltip-content"
              (mouseenter)="onTooltipMouseEnter()"
              (mouseleave)="onTooltipMouseLeave()"
              (focusin)="onTooltipMouseEnter()"
              (focusout)="onTooltipMouseLeave()"
            >
              <ul>
                <li>{{ strings.tooltip.permanentRules }}</li>
                <li>{{ strings.tooltip.temporaryRules }}</li>
                <li>{{ strings.tooltip.simultaneousRules }}</li>
                <li>
                  {{ strings.tooltip.questions }}
                  <a [href]="strings.tooltip.consultManualUrl" (click)="onClickConsult($event)">{{
                    strings.tooltip.consultManual
                  }}</a>
                  {{ strings.tooltip.or }}
                  <a [href]="strings.tooltip.contactUrl" (click)="onClickContact($event)">{{
                    strings.tooltip.contactSpecialist
                  }}</a>
                </li>
              </ul>
            </div>
          </ng-template>
        </mat-icon>
      }
    </h2>
    <div>
      @if (showScheduleRemove() && scheduleService().isFilledAndChanged() && !readonly()) {
        <button
          mat-icon-button
          (click)="onClickRemove()"
          [matTooltip]="strings.buttons.discardChanges"
          [disabled]="isDisabled()"
        >
          <mat-icon color="warn">close</mat-icon>
        </button>
      }
      @if (!readonly() && allowEmptySchedule() && !scheduleService().isEmptySchedule) {
        <button
          mat-button
          (click)="onClickRemoveEmpty()"
          [matTooltip]="strings.buttons.removeExistingSchedule"
          [disabled]="isDisabled()"
        >
          <mat-icon [color]="!scheduleService().isEmptySchedule ? 'warn' : undefined"
            >delete
          </mat-icon>
          {{ strings.buttons.removeSchedule }}
        </button>
      }
      @if (showReset() && !isDisabled() && allowEmptySchedule()) {
        <button
          mat-icon-button
          (click)="onClickRemoveEmpty()"
          [matTooltip]="strings.buttons.resetScheduleToFactory"
          [disabled]="isDisabled()"
        >
          <mat-icon color="warn">undo</mat-icon>
        </button>
      }
    </div>
  </div>
  @if (!readonly() && errors()?.length) {
    <div class="generic-schedule__errors">
      @for (error of errors(); track error) {
        <vectronic-alert type="error">
          <span>{{ error }}</span>
        </vectronic-alert>
      }
    </div>
  }
  @for (warning of warningMessages(); track warning) {
    <vectronic-alert type="warning">{{ warning }}</vectronic-alert>
  }
  @for (error of errorMessages(); track error) {
    <vectronic-alert type="error">{{ error }}</vectronic-alert>
  }
  @if (showOverrideWarning() && !scheduleService().isEmptySchedule) {
    <div class="generic-schedule__override-warning">
      <vectronic-alert type="warning">
        <div [innerHTML]="strings.warnings.overwriteWarning(scheduleType())"></div>
      </vectronic-alert>
    </div>
  }
  @if (
    scheduleService().isEmptySchedule && ((scheduleService().isChanged$ | async) || readonly())
  ) {
    <vectronic-alert type="warning">
      {{ strings.warnings.removingScheduleWarning }}
    </vectronic-alert>
    <div>{{ strings.warnings.removingScheduleDescription }}</div>
  }

  @if (
    !hideRules() &&
    (scheduleService().isScheduleExpanded() ||
      readonly() ||
      (scheduleService().hasDefaultValue() &&
        (!allowEmptySchedule() || !scheduleService().isEmptySchedule)))
  ) {
    <div class="generic-schedule__wrapper" [attr.data-testid]="'generic-schedule'">
      <div
        class="generic-schedule__left-side"
        [ngClass]="{
          'generic-schedule__force-show-calendar': scheduleService().forceShowCalendar(),
        }"
      >
        @let indices =
          hideChangedSchedules()
            ? scheduleService().defaultRulesIndexes$.value
            : scheduleService().rulesIndexes$.value;
        @for (ruleIndex of indices; track ruleIndex; let index = $index) {
          <rc-rule
            [index]="ruleIndex"
            (remove)="onRemoveRule($event)"
            [scheduleService]="scheduleService()"
            [readonly]="readonly() || isDisabled()"
            [hasTransmissionCount]="hasTransmissionCount()"
            [maxTransmissionCount]="maxTransmissionCount()"
            [order]="index"
            [hasDefaultSkipCount]="hasDefaultSkipCount()"
            [hideChangedSchedules]="hideChangedSchedules()"
            [permanentStartDate]="permanentStartDate()"
            [permanentEndDate]="permanentEndDate()"
            [isRulesDeletable]="isRulesDeletable()"
            [isNoCommunication]="isNoCommunication()"
          ></rc-rule>
        }
        @if (
          !readonly() &&
          !isDisabled() &&
          (!allowEmptySchedule() || !scheduleService().isEmptySchedule)
        ) {
          <div class="generic-schedule__action-button">
            <button
              [attr.data-testid]="'add-rule-button'"
              mat-button
              class="generic-schedule__rule-action"
              [disabled]="isDisabled()"
              (click)="onClickAddRule()"
            >
              <mat-icon color="accent">add</mat-icon>
              {{ strings.buttons.addRule }}
            </button>
            @if (
              !readonly() && (showLoadFromTemplates() || showImportJson() || showLoadGpsXFile())
            ) {
              <button
                mat-button
                cdkOverlayOrigin
                #loadTriggerExpanded="cdkOverlayOrigin"
                [matMenuTriggerFor]="loadScheduleMenu"
                (click)="onLoadMenuClick(loadTriggerExpanded)"
                [attr.data-testid]="'load-schedule-button'"
                [disabled]="isDisabled()"
              >
                {{ strings.buttons.loadSchedule }}
                <mat-icon color="accent">expand_more</mat-icon>
              </button>
            }
            @if (
              !readonly() &&
              scheduleService().isFilled() &&
              scheduleService().isValid() &&
              (showSaveAsTemplate() || canExportJson())
            ) {
              <button
                mat-button
                cdkOverlayOrigin
                #saveTrigger="cdkOverlayOrigin"
                [matMenuTriggerFor]="saveScheduleMenu"
                (click)="onSaveMenuClick(saveTrigger)"
                [attr.data-testid]="'save-schedule-button'"
                [disabled]="isDisabled()"
              >
                {{ strings.buttons.saveAs }}
                <mat-icon color="accent">expand_more</mat-icon>
              </button>
            }
          </div>
        }
      </div>

      <div class="generic-schedule__preview">
        @if (
          (scheduleService().isFilled() || scheduleService().forceShowCalendar()) &&
          (!scheduleService().config?.hasEmptySchedule || !scheduleService().isEmptySchedule)
        ) {
          <rc-calendar [scheduleType]="scheduleType()" [(showPreview)]="showPreview"></rc-calendar>
        }
      </div>
    </div>
  } @else if (!hideRules()) {
    <div class="generic-schedule__action-button">
      <button
        mat-button
        (click)="onAddSchedule()"
        [attr.data-testid]="'add-schedule-button'"
        [disabled]="isDisabled()"
      >
        @if (allowEmptySchedule() && scheduleService().isEmptySchedule) {
          {{ strings.buttons.addRule }}
        } @else {
          {{ addScheduleText() }}
        }
        <mat-icon color="accent">add</mat-icon>
      </button>
      @if (showLoadFromTemplates() || showImportJson() || showLoadGpsXFile()) {
        <button
          mat-button
          cdkOverlayOrigin
          #loadTriggerCollapsed="cdkOverlayOrigin"
          [matMenuTriggerFor]="loadScheduleMenu"
          (click)="onLoadMenuClick(loadTriggerCollapsed)"
          [attr.data-testid]="'load-schedule-button'"
          [disabled]="isDisabled()"
        >
          {{ strings.buttons.loadSchedule }}
          <mat-icon color="accent">expand_more</mat-icon>
        </button>
      }
    </div>
  }

  @if (
    !isMaxSchedulesReachedAndIsNotFilled() &&
    isDisabled() &&
    allowedCollars()?.length &&
    !readonly() &&
    !isDisabledByEmptySchedule()
  ) {
    <vectronic-alert type="error">
      {{ strings.errors.scheduleNotAvailable(allowedCollars()?.join(',') ?? '') }}
    </vectronic-alert>
  }
</div>
<mat-divider></mat-divider>

@if (showLoadFromTemplates()) {
  <ng-template
    cdkConnectedOverlay
    [cdkConnectedOverlayOrigin]="loadScheduleTrigger()"
    [cdkConnectedOverlayOpen]="isLoadScheduleOpen()"
    (overlayOutsideClick)="isLoadScheduleOpen.set(false)"
  >
    <div class="generic-schedule__load-overlay">
      <rc-schedule-template-selector
        [scheduleType]="scheduleType()"
        (templateSelected)="onTemplateSelected($event)"
      ></rc-schedule-template-selector>
    </div>
  </ng-template>
}

@if (isSaveScheduleOpen()) {
  <ng-template
    cdkConnectedOverlay
    [cdkConnectedOverlayOrigin]="saveScheduleTrigger()"
    [cdkConnectedOverlayOpen]="isSaveScheduleOpen()"
    (overlayOutsideClick)="isSaveScheduleOpen.set(false)"
  >
    <div class="generic-schedule__load-overlay generic-schedule__save-overlay">
      <mat-form-field appearance="outline">
        <mat-label>{{ strings.form.templateNameLabel }}</mat-label>
        <input
          matInput
          [ngModel]="templateName()"
          (ngModelChange)="templateName.set($event)"
          (keydown.enter)="onConfirmSaveTemplate()"
        />
      </mat-form-field>
      <button
        mat-button
        (click)="onConfirmSaveTemplate()"
        [disabled]="!templateName() || isDisabled()"
        color="accent"
      >
        {{ strings.buttons.saveTemplate }}
      </button>
    </div>
  </ng-template>
}

<input
  #importJsonInput
  type="file"
  accept=".json,application/json"
  hidden
  (change)="onImportJsonSelected($event)"
/>
<input
  #gpsXFileInput
  type="file"
  [accept]="gpsXFileAccept()"
  hidden
  (change)="onLoadGpsXFileSelected($event)"
/>

<mat-menu #loadScheduleMenu="matMenu">
  @if (showLoadFromTemplates()) {
    <button mat-menu-item (click)="onOpenTemplateSelector()">
      <mat-icon color="accent">inventory_2</mat-icon>
      {{ strings.buttons.loadFromTemplate }}
    </button>
  }
  @if (showImportJson()) {
    <button mat-menu-item (click)="onImportJson()">
      <mat-icon color="accent">upload_file</mat-icon>
      {{ strings.buttons.importJson }}
    </button>
  }
  @if (showLoadGpsXFile()) {
    <button mat-menu-item (click)="onLoadGpsXFile()" [disabled]="gpsXFileImportService.loading()">
      <mat-icon color="accent">upload_file</mat-icon>
      {{ strings.buttons.loadGpsXFile }}
    </button>
  }
</mat-menu>

<mat-menu #saveScheduleMenu="matMenu">
  @if (showSaveAsTemplate()) {
    <button mat-menu-item (click)="onOpenSaveTemplate()">
      <mat-icon color="accent">save</mat-icon>
      {{ strings.buttons.saveAsTemplate }}
    </button>
  }
  <button mat-menu-item (click)="onExportJson()" [attr.data-testid]="'export-schedule-button'">
    <mat-icon color="accent">file_download</mat-icon>
    {{ strings.buttons.exportJson }}
  </button>
</mat-menu>
